// Üst şeritler: deneme bitiyor / grace / salt okunur / kapalı / ücretli bitişe az kaldı.
// `access` yoksa (eski sunucu) eski subscriptionEndDate şeritleri aynen gösterilir.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Lock } from 'lucide-react';
import { usePlan } from '../lib/plan-context';
import { useI18n } from '../contexts/LanguageContext';
import { graceDaysLeft, planUiText, showTrialBanner, showTrialExpiryBanner } from '../lib/plan-texts';

const n = (s: string, v: number) => s.split('{n}').join(String(v));

function Banner({ tone, icon: Icon = AlertTriangle, text, action, children }: {
  tone: 'red' | 'orange';
  icon?: typeof AlertTriangle;
  text: string;
  action: string;
  children?: ReactNode;
}) {
  const bg = tone === 'red' ? 'bg-red-600' : 'bg-orange-500';
  const btn = tone === 'red' ? 'text-red-600 hover:bg-red-50' : 'text-orange-600 hover:bg-orange-50';
  return (
    <div className={`${bg} text-white px-4 py-2 flex items-center justify-between gap-3`} role="status">
      <div className="flex items-start gap-2 min-w-0">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <span className="text-sm font-medium">{text}</span>
          {children}
        </div>
      </div>
      <Link to="/subscription" className={`bg-white ${btn} font-semibold px-3 py-1 rounded text-sm flex-shrink-0`}>
        {action} →
      </Link>
    </div>
  );
}

export function PlanBanners() {
  const { access, subscriptionEndDate } = usePlan();
  const { language } = useI18n();
  const ui = planUiText(language);

  if (!access) return <LegacySubscriptionBanners endDate={subscriptionEndDate} />;

  switch (access.tier) {
    case 'closed':
      return <Banner tone="red" icon={Lock} text={ui.closedBanner} action={ui.renew} />;
    case 'read_only':
      return <Banner tone="orange" icon={Lock} text={ui.readOnlyBanner} action={ui.renew} />;
    case 'grace':
      return <Banner tone="red" text={n(ui.graceBanner, graceDaysLeft(access))} action={ui.renew} />;
    case 'trial':
      // 4–7 gün: eski turuncu "bitişe X gün" şeridi (≤3 günde aşağıdaki "Deneme bitiyor" şeridi; ikisi birlikte çıkmaz)
      if (showTrialExpiryBanner(access)) {
        return <Banner tone="orange" text={n(ui.paidEnding, access.daysLeft ?? 0)} action={ui.renew} />;
      }
      if (!showTrialBanner(access)) return null;
      return (
        <Banner tone="orange" text={n(ui.trialEnding, Math.max(0, access.daysLeft ?? 0))} action={ui.seePlans}>
          <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-white/95">
            {ui.trialLoses.map((item) => <li key={item}>✕ {item}</li>)}
          </ul>
          <div className="mt-1 text-xs text-white/90">
            <span className="font-medium">{ui.freeIntro}</span>{' '}
            {ui.freeLimits.join(' · ')}
          </div>
        </Banner>
      );
    case 'paid':
      // Ücretli: bugünkü "bitişe 7 gün" uyarısı aynen
      if (typeof access.daysLeft !== 'number' || access.daysLeft > 7) return null;
      return <Banner tone="orange" text={n(ui.paidEnding, Math.max(0, access.daysLeft))} action={ui.renew} />;
    default:
      return null;
  }
}

/** Eski davranış (sunucu `access` döndürmüyorsa) */
function LegacySubscriptionBanners({ endDate }: { endDate: string | null }) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  if (end < new Date()) {
    return (
      <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">
            Aboneliğiniz {end.toLocaleDateString('tr-TR')} tarihinde sona erdi. Yeni sipariş oluşturamazsınız.
          </span>
        </div>
        <Link to="/subscription" className="bg-white text-red-600 font-semibold px-3 py-1 rounded text-sm hover:bg-red-50 flex-shrink-0">
          Yenile →
        </Link>
      </div>
    );
  }
  const daysLeft = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysLeft > 7) return null;
  return (
    <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium">
          Aboneliğiniz {daysLeft} gün içinde sona eriyor ({end.toLocaleDateString('tr-TR')}).
        </span>
      </div>
      <Link to="/subscription" className="bg-white text-orange-600 font-semibold px-3 py-1 rounded text-sm hover:bg-orange-50 flex-shrink-0">
        Yenile →
      </Link>
    </div>
  );
}
